import type { BuildingGraph } from "building-types/building/buildingGraphTypes";
import type { Building } from "building-types/building/buildingTypes";
import { tagBuildingFace } from "building-types/buildingGeomTag/buildingGeomTags";
import type { BuildingGeomParent, BuildingGeomParentType } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import { LateralDatumJustification, WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import { FloorAssemblyType } from "building-types/floors/floorAssemblyTypes";
import { StructuralFloorType } from "building-types/floors/floorComponentsTypes";
import type { FoundationAssembly } from "building-types/foundations/foundationAssemblyEntityTypes";
import { FoundationAssemblyType } from "building-types/foundations/foundationAssemblyTypesEnum";
import { FoundationCondition } from "building-types/foundations/foundationEnums";
import type { WallPlaneRuleHandle } from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomKind, RoomEnclosure, RoomClass, RoomSupport, RoomCover } from "building-types/rooms/roomTypesEnum";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { FreeformWallEntity } from "building-types/walls2/freeformWallTypes";
import type {
  WallPlane,
  WallPlaneSubdomain,
  WallPlaneSubdomainMiterMass,
  WallPlaneDescriptor,
  WallPlaneDomainOperation,
  WallPlaneCoreDirection,
} from "building-types/walls2/wallPlane";
import type { WallPlaneAssemblyEntity, WallPlaneAssemblyLayer } from "building-types/walls2/wallPlaneAssemblyTypes";
import { PathPriority } from "building-types/walls/pathPriority";
import type {
  WallAssembly,
  WallAssemblyCladdingLayer,
  WallAssemblyLayer,
  WallAssemblyLayerParentHandle,
  WallAssemblyStructuralLayer,
} from "building-types/walls/wallAssemblyTypes";
import type {
  WallDescriptor,
  WallOperators,
  WallSection,
  WallSideDescriptor,
  WallSideRoomDescriptor,
} from "building-types/walls/wallDesciptorTypes";
import { StructuralAssemblyType, WallCladdingType } from "building-types/walls/wallDesciptorTypes";
import { WallLayerType, WallType } from "building-types/walls/wallEnums";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import { WallCondition } from "building-types/walls/wallType";
import type { WallLayer, WallMass, WallPath, SolidWallMass } from "building-types/walls/wallTypes";
import { FREEFORM_WALL_HALF_EDGE_INDEX } from "building-types/walls/wallTypes";
import type { Table, RWTable } from "common/data/table";
import type { LevelInches } from "common/types/nominalNumbersTypes";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { levelInchesNew } from "common/utils/nominalNumbers";
import { sortTableBySortIndex } from "common/utils/sortIndex";
import uid from "common/utils/uid";
import { foundationLateralDatumEntityNew } from "doc/datums/datumEntityNew";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import { getOpenConnectedRoomSets } from "doc/rooms/roomTraversal";
import { pathPriorityOpposite } from "doc/utils/wallTools";
import { aabb2Grow, aabb2Copy } from "geom/aabb2";
import { aabb2New } from "geom/aabb2Types";
import { closestParamNormalizedLine2 } from "geom/closestPoint";
import { DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import { line2Len, line2Tangent } from "geom/line2";
import { planarGraphAdjacentHalfEdge, planarGraphHalfEdgeStart, planarGraphHalfEdgeEnd } from "geom/planarGraph";
import { planeNewXY, planeHash, planeProject, planeEval, planeCanonicalFromSegment } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { vec2Normal } from "geom/polyclip/vec2";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import { polyLine2EdgeCount, polyLine2EdgeStart, polyLine2EdgeEnd } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { vec2DistSquared, vec2Dot } from "geom/vec2";
import { LegacyFoundationLateralDatumAbstraction } from "../elements/datums/datumAbstractions";
import { collectRoomConnectionsOnLevel } from "../elements/rooms/collectRoomConnectionsOnLevel";
import { wallAssemblyAirGapLayerNew, wallAssemblyNew } from "../elements/wallAssemblies/wallAssembly";
import { determineWallCondition, resolveWallPlaneSideRoomsFromFaceNodes } from "../elements/walls2/wallConditionTools";
import {
  wallPlaneAssemblyLayerToLegacyWallCladdingType,
  wallPlaneAssemblyLayerToLegacyMaterialCatalogLayer,
  wallPlaneAssemblyLayerToLegacyFinishLayer,
  wallPlaneAssemblyLayerHasFinish,
} from "../elements/walls2/wallPlaneAssemblyLegacyConversionTools";
import { subdomainGetFreeformWallSegment } from "../elements/walls2/wallPlaneSubdomainRoomFilter";
import { solidWallMassFromWallEnds } from "../elements/walls/solidWall";
import { voidWallFromHalfEdgeOffsets } from "../elements/walls/voidWall";
import { wallLayerNew } from "../elements/walls/wall";
import { wallDescriptorOpposite } from "../elements/walls/wallDescriptor";
import { wallEndCapNew } from "../elements/walls/wallEnds";
import { WallIndexImpl } from "../elements/walls/wallIndexImpl";

/**
 * This function is intended to be a drop in replacement
 * for the existing wall mass generation. At this time, it is
 * OK for it to stub out the return types in order to comply
 * with downstream building generation expectations.
 */
export function generateLevelWallMassesFromWallPlanes({
  levels,
  levelPlanarGraphs,
  buildingGraph,
  wallPlaneMasses,
  freeformWallPlaneMasses = [],
  freeformWalls = {},
  wallPlanes,
  issues,
  rooms,
  roomsByLevel,
  roomLevelIndices,
}: {
  levels: Table<Level>;
  levelPlanarGraphs: Table<PolygonPlanarGraph>;
  buildingGraph: BuildingGraph;
  wallPlaneMasses: WallPlaneSubdomainMiterMass[];
  freeformWallPlaneMasses?: WallPlaneSubdomainMiterMass[];
  freeformWalls?: Table<FreeformWallEntity>;
  wallPlanes: Table<WallPlane>;
  rooms: Table<Room>;
  roomsByLevel: Table<string[]>;
  roomLevelIndices: Table<number>;
  issues: BuildingGenerationIssue[];
}): Pick<Building, "wallMasses" | "wallLayers" | "datumAbstractions" | "issues"> {
  const wallMasses: RWTable<WallMassSystem> = {};
  const wallLayers: RWTable<WallLayer> = {};
  const datumAbstractions: RWTable<DatumAbstraction> = {};

  /**
   * @NOTE - we collect all the wall plane masses by level upfront, since we don't
   * want a wall plane mass to be associated with multiple levels, and this function performs
   * a bfs for masses that don't have a direct lookup
   */
  const { levelWallPlaneMasses, assignedSubdomains } = collectWallPlaneSubdomainMiterMassesForLevels(
    levels,
    levelPlanarGraphs,
    wallPlaneMasses,
    wallPlanes,
  );

  const freeformMassesByLevel = collectFreeformWallPlaneMassesByLevel(
    freeformWallPlaneMasses,
    freeformWalls,
    buildingGraph,
    assignedSubdomains,
  );

  for (const levelId in levels) {
    const level = levels[levelId];
    const levelPlanarGraph = levelPlanarGraphs[levelId];
    const levelWallPlaneMassesForLevel = levelWallPlaneMasses[levelId];
    wallMasses[levelId] = makeLevelWallMassSystemFromWallPlanes(
      level,
      levelPlanarGraph,
      levelWallPlaneMassesForLevel,
      freeformMassesByLevel.get(levelId) ?? new Map(),
      wallPlanes,
      buildingGraph,
      rooms,
      roomsByLevel,
      roomLevelIndices,
    );
  }

  return {
    wallMasses,
    wallLayers,
    datumAbstractions,
    issues,
  };
}

const LEVEL_XY_PLANE = planeNewXY();

type LevelHalfEdgeInfo = { levelId: string; halfEdgeIndex: number };
type LevelHalfEdgeInfoLookup = Map<WallPlaneSubdomain, LevelHalfEdgeInfo>;
type LevelHalfEdgeToSubdomainLookup = Map<number, WallPlaneSubdomain[]>;

function collectWallPlaneSubdomainMiterMassesForLevels(
  levels: Table<Level>,
  levelPlanarGraphs: Table<PolygonPlanarGraph>,
  wallPlaneMasses: WallPlaneSubdomainMiterMass[],
  wallPlanes: Table<WallPlane>,
) {
  const levelWallPlaneMasses: RWTable<Map<number, WallPlaneSubdomainMiterMass[]>> = {};
  const levelWallPlaneSubdomains: RWTable<Map<number, WallPlaneSubdomain[]>> = {};
  const wallPlaneSubdomainToLevelHalfEdgeInfo: LevelHalfEdgeInfoLookup = new Map();
  const assignedSubdomains = new Set<WallPlaneSubdomain>();

  // first, index the subdomains by level and half edge index for fast lookup
  // this is used to quickly find the level and half edge index for a given subdomain
  for (const levelId in levels) {
    const level = levels[levelId];
    const levelPlanarGraph = levelPlanarGraphs[levelId];
    const levelSubdomains = collectWallPlaneSubdomainsForLevel(level, levelPlanarGraph, wallPlanes);
    levelWallPlaneSubdomains[levelId] = levelSubdomains;
    levelWallPlaneMasses[levelId] = new Map<number, WallPlaneSubdomainMiterMass[]>();

    // build the lookup tables and populate the return map
    // index the subdomains by level and half edge index
    for (const halfEdgeIndex of levelSubdomains.keys()) {
      levelWallPlaneMasses[levelId].set(halfEdgeIndex, []);
      const halfEdgeSubdomains = levelSubdomains.get(halfEdgeIndex);
      assertIsNotNullOrUndefined(halfEdgeSubdomains);

      for (const subdomain of halfEdgeSubdomains) {
        wallPlaneSubdomainToLevelHalfEdgeInfo.set(subdomain, { levelId, halfEdgeIndex });
      }
    }
  }

  // next, iterate all the wall core masses and find their appropriate level and half edge index
  // if a direct lookup is found, add to that array, else
  // search neighboring subdomains until we hit a match and add to that array
  for (const wallPlaneMass of wallPlaneMasses) {
    const { subdomain } = wallPlaneMass.miterEdge;
    const lookupInfo = wallPlaneSubdomainToLevelHalfEdgeInfo.get(subdomain);
    // fast path - the subdomain is already in the lookup
    if (lookupInfo) {
      const { levelId, halfEdgeIndex } = lookupInfo;
      const arr = levelWallPlaneMasses[levelId].get(halfEdgeIndex);
      assertIsNotNullOrUndefined(arr);

      arr.push(wallPlaneMass);
      assignedSubdomains.add(subdomain);
      continue;
    }

    // slow path - we need to trace the subdomain to find the level and half edge index
    const wallPlane = wallPlanes[wallPlaneMass.miterEdge.wallPlaneHash];
    assertIsNotNullOrUndefined(wallPlane);

    const wallPlaneDescriptor = wallPlane.descriptor;
    const wallPlaneTraceLookup = findLevelHalfEdgeInfoBySubdomainBfs(
      wallPlaneDescriptor.subdomains,
      wallPlaneSubdomainToLevelHalfEdgeInfo,
      subdomain.arrayIndexU,
      subdomain.arrayIndexV,
    );
    if (!wallPlaneTraceLookup) {
      // DEBUG - uncomment this to see which subdomains are not being found
      // console.warn("no wall core trace lookup found for subdomain", subdomain);
      continue;
    }

    const { levelId, halfEdgeIndex } = wallPlaneTraceLookup;
    const arr = levelWallPlaneMasses[levelId].get(halfEdgeIndex);
    assertIsNotNullOrUndefined(arr);
    arr.push(wallPlaneMass);
    assignedSubdomains.add(subdomain);

    // add the subdomain to the lookup table for faster future lookups
    wallPlaneSubdomainToLevelHalfEdgeInfo.set(subdomain, { levelId, halfEdgeIndex });
  }

  return { levelWallPlaneMasses, assignedSubdomains };
}

/**
 * Group freeform-associated miter masses by level and freeform segment.
 * Caller is responsible for excluding room-owned cells (rule-winner ownership); this
 * only skips subdomains already emitted on a room half-edge as a safety net.
 */
function collectFreeformWallPlaneMassesByLevel(
  freeformWallPlaneMasses: WallPlaneSubdomainMiterMass[],
  freeformWalls: Table<FreeformWallEntity>,
  buildingGraph: BuildingGraph,
  assignedSubdomains: ReadonlySet<WallPlaneSubdomain>,
): Map<string, Map<string, WallPlaneSubdomainMiterMass[]>> {
  const byLevel = new Map<string, Map<string, WallPlaneSubdomainMiterMass[]>>();

  for (const mass of freeformWallPlaneMasses) {
    const { subdomain } = mass.miterEdge;
    if (assignedSubdomains.has(subdomain)) {
      continue;
    }
    if (!subdomain.wallInfo) {
      continue;
    }

    const segment = subdomainGetFreeformWallSegment(subdomain, buildingGraph);
    if (!segment) {
      continue;
    }

    const freeformWall = freeformWalls[segment.freeformWallId];
    if (!freeformWall) {
      continue;
    }

    const levelId = freeformWall.levelId;
    const segmentKey = `${segment.freeformWallId}:${segment.edgeId}`;
    let levelMap = byLevel.get(levelId);
    if (!levelMap) {
      levelMap = new Map();
      byLevel.set(levelId, levelMap);
    }
    const arr = levelMap.get(segmentKey);
    if (arr) {
      arr.push(mass);
    } else {
      levelMap.set(segmentKey, [mass]);
    }
  }

  return byLevel;
}

function collectWallPlaneSubdomainsForLevel(
  level: Level,
  levelPlanarGraph: PolygonPlanarGraph,
  wallPlanes: Table<WallPlane>,
): LevelHalfEdgeToSubdomainLookup {
  const { halfEdges } = levelPlanarGraph.planarGraph;

  const levelBottomDatum = level.floorElevation - level.floorPlateThickness;
  const levelTopDatum = level.floorElevation + level.topOfWall;

  const halfEdgeToWallPlaneMasses: LevelHalfEdgeToSubdomainLookup = new Map();

  // for each planar graph half edge, detect the canonical wall plane for that edge
  // and collect the subdomains that are associated with that half edge within the domain
  // of the level bottom and top datums
  for (let i = 0; i < halfEdges.length; i += 2) {
    const leftHalfEdge = i / 2;
    if (halfEdgeToWallPlaneMasses.has(leftHalfEdge)) {
      continue;
    }

    const [startX, startY] = planarGraphHalfEdgeStart(levelPlanarGraph.planarGraph, leftHalfEdge);
    const [endX, endY] = planarGraphHalfEdgeEnd(levelPlanarGraph.planarGraph, leftHalfEdge);

    const halfEdgeCannonicalPlane = planeCanonicalFromSegment(startX, startY, endX, endY, LEVEL_XY_PLANE);
    if (!halfEdgeCannonicalPlane) {
      continue;
    }

    const halfEdgeCannonicalPlaneHash = planeHash(halfEdgeCannonicalPlane);
    const halfEdgeWallPlane = wallPlanes[halfEdgeCannonicalPlaneHash];
    if (!halfEdgeWallPlane) {
      continue;
    }
    const [pStartX, pStartY] = planeProject(halfEdgeCannonicalPlane, startX, startY, levelBottomDatum);
    const [pEndX, pEndY] = planeProject(halfEdgeCannonicalPlane, endX, endY, levelTopDatum);

    const pMinX = Math.min(pStartX, pEndX);
    const pMinY = Math.min(pStartY, pEndY);
    const pMaxX = Math.max(pStartX, pEndX);
    const pMaxY = Math.max(pStartY, pEndY);

    const halfEdgeToCannonicalAABB = aabb2New(pMinX, pMinY, pMaxX, pMaxY);
    aabb2Grow(halfEdgeToCannonicalAABB, -DIST_EPSILON, -DIST_EPSILON);

    const halfEdgeWallDescriptor = halfEdgeWallPlane.descriptor;
    const halfEdgeDomains = halfEdgeWallDescriptor.subdomainIndex.search(halfEdgeToCannonicalAABB);
    halfEdgeToWallPlaneMasses.set(leftHalfEdge, halfEdgeDomains);

    const rightHalfEdge = planarGraphAdjacentHalfEdge(leftHalfEdge);
    halfEdgeToWallPlaneMasses.set(rightHalfEdge, halfEdgeDomains);
  }

  return halfEdgeToWallPlaneMasses;
}

function findLevelHalfEdgeInfoBySubdomainBfs(
  subdomains: WallPlaneSubdomain[][],
  subdomainLookup: LevelHalfEdgeInfoLookup,
  startIndexU: number,
  startIndexV: number,
): LevelHalfEdgeInfo | null {
  /**
   * We store the wallPlaneSubdomains as both a RBush of the domains, but also
   * as a 2d array. this means we can trace in all directions from a given subdomain.
   * to find adjacent subdomains
   */
  const rowCount = subdomains.length;
  if (rowCount === 0) {
    return null;
  }

  const firstRow = subdomains[0];
  if (!firstRow || firstRow.length === 0) {
    return null;
  }

  const colCount = firstRow.length;
  if (colCount === 0) {
    return null;
  }

  if (startIndexV < 0 || startIndexV >= rowCount || startIndexU < 0 || startIndexU >= colCount) {
    return null;
  }

  const queue: Array<[number, number]> = [[startIndexV, startIndexU]];
  const visited = new Set<number>();
  let queueIndex = 0;
  while (queueIndex < queue.length) {
    const [v, u] = queue[queueIndex];
    queueIndex += 1;

    const key = v * colCount + u;
    if (visited.has(key)) {
      continue;
    }
    visited.add(key);

    const nextSubdomain = subdomains[v][u];
    const nextLevelHalfEdgeInfo = subdomainLookup.get(nextSubdomain);
    if (nextLevelHalfEdgeInfo) {
      return nextLevelHalfEdgeInfo;
    }

    // prioritize vertical traversal, then horizontal
    if (v > 0) {
      queue.push([v - 1, u]);
    }
    if (v + 1 < rowCount) {
      queue.push([v + 1, u]);
    }
    if (u > 0) {
      queue.push([v, u - 1]);
    }
    if (u + 1 < colCount) {
      queue.push([v, u + 1]);
    }
  }

  return null;
}

function makeLevelWallMassSystemFromWallPlanes(
  level: Level,
  levelPlanarGraph: PolygonPlanarGraph,
  levelWallPlaneMassesByHalfEdge: Map<number, WallPlaneSubdomainMiterMass[]>,
  freeformMassesBySegment: Map<string, WallPlaneSubdomainMiterMass[]>,
  wallPlanes: Table<WallPlane>,
  buildingGraph: BuildingGraph,
  rooms: Table<Room>,
  roomsByLevel: Table<string[]>,
  roomLevelIndices: Table<number>,
): WallMassSystem {
  const levelId = level.id;
  const { halfEdges } = levelPlanarGraph.planarGraph;

  const wallMasses: RWTable<WallMass> = {};
  const halfEdgeToWallMass = new Array<string>(halfEdges.length);

  for (let i = 0, l = halfEdges.length; i < l; i += 2) {
    const halfEdgeIndex = i / 2;
    const oppHalfEdgeIndex = planarGraphAdjacentHalfEdge(halfEdgeIndex);
    if (halfEdgeToWallMass[halfEdgeIndex] || halfEdgeToWallMass[oppHalfEdgeIndex]) {
      continue;
    }

    const halfEdgeWallPlaneMasses = levelWallPlaneMassesByHalfEdge.get(halfEdgeIndex) ?? [];
    const oppHalfEdgeWallPlaneMasses = levelWallPlaneMassesByHalfEdge.get(oppHalfEdgeIndex) ?? [];

    const allHalfEdgeWallPlaneMasses = [...halfEdgeWallPlaneMasses, ...oppHalfEdgeWallPlaneMasses];
    const wallMass = makeLegacyWallMassFromWallPlaneSubdomainMiterMasses(
      level,
      halfEdgeIndex,
      allHalfEdgeWallPlaneMasses,
      wallPlanes,
      buildingGraph,
      rooms,
      levelPlanarGraph,
    );
    if (!wallMass) {
      continue;
    }

    wallMasses[wallMass.id] = wallMass;
    halfEdgeToWallMass[halfEdgeIndex] = wallMass.id;
    halfEdgeToWallMass[oppHalfEdgeIndex] = wallMass.id;
  }

  for (const segmentMasses of freeformMassesBySegment.values()) {
    if (segmentMasses.length === 0) {
      continue;
    }
    const wallMass = makeLegacyWallMassFromWallPlaneSubdomainMiterMasses(
      level,
      FREEFORM_WALL_HALF_EDGE_INDEX,
      segmentMasses,
      wallPlanes,
      buildingGraph,
      rooms,
      undefined,
    );
    if (wallMass) {
      wallMasses[wallMass.id] = wallMass;
    }
  }

  const levelRooms = roomsOnLevel(levelId, rooms, roomsByLevel);
  const levelRoomConnections = collectRoomConnectionsOnLevel({
    openFullDoors: [], // TODO use void domains
    levelId,
    roomsByLevel,
    roomLevelIndices,
  });

  const levelOpenConnectedRoomSets = getOpenConnectedRoomSets(levelRooms, levelRoomConnections, roomLevelIndices);
  const levelWallIndex = new WallIndexImpl(wallMasses, halfEdgeToWallMass, roomLevelIndices, levelPlanarGraph);

  return {
    wallIndex: levelWallIndex,
    walls: wallMasses,
    halfEdgeToWall: halfEdgeToWallMass,
    openConnectedRoomSets: levelOpenConnectedRoomSets,
  };
}

const wallCorePredicate: WallPlaneSubdomainOffsetPredicate = (input) => {
  return {
    leftOffset: input.miterEdge.leftCoreOffset,
    rightOffset: input.miterEdge.rightCoreOffset,
    leftEdge: input.leftCoreEdge,
    rightEdge: input.rightCoreEdge,
  };
};

const wallCladdingPredicate: WallPlaneSubdomainOffsetPredicate = (input) => {
  return {
    leftOffset: input.miterEdge.leftCladdingOffset,
    rightOffset: input.miterEdge.rightCladdingOffset,
    leftEdge: input.leftCladdingEdge,
    rightEdge: input.rightCladdingEdge,
  };
};

/**
 * Builds a legacy `WallMass` from wall-plane subdomain miter masses, either for a room
 * planar-graph half-edge (`levelPlanarGraph` provided) or a freeform wall segment
 * (`halfEdgeIndex === FREEFORM_WALL_HALF_EDGE_INDEX`, `levelPlanarGraph` undefined).
 */
function makeLegacyWallMassFromWallPlaneSubdomainMiterMasses(
  level: Level,
  halfEdgeIndex: number,
  wallPlaneMasses: WallPlaneSubdomainMiterMass[],
  wallPlanes: Table<WallPlane>,
  buildingGraph: BuildingGraph,
  rooms: Table<Room>,
  levelPlanarGraph: PolygonPlanarGraph | undefined,
): WallMass | null {
  const isFreeform = halfEdgeIndex === FREEFORM_WALL_HALF_EDGE_INDEX;
  if (isFreeform && wallPlaneMasses.length === 0) {
    return null;
  }

  // replicate legacy wall mass id generation for now
  // each wall mass gets a unique id shared accross all layers
  const wallId = uid();

  // compute all the sub masses upfront
  const legacyCoreMasses: WallMass[] = [];
  const legacyCladdingMasses: WallMass[] = [];
  const allRoofClippingOps: WallPlaneDomainOperation[] = [];
  const roofClippingOpsBySubMassId = new Map<string, WallPlaneDomainOperation[]>();
  const allStairClippingOps: WallPlaneDomainOperation[] = [];
  const stairClippingOpsBySubMassId = new Map<string, WallPlaneDomainOperation[]>();

  for (const wallPlaneMass of wallPlaneMasses) {
    const wallPlaneHash = wallPlaneMass.miterEdge.wallPlaneHash;
    const wallPlane = wallPlanes[wallPlaneHash];
    const buildingPlane = buildingGraph.buildingPlanes[wallPlaneHash];
    if (!wallPlane || !buildingPlane) {
      continue;
    }

    const { subdomain } = wallPlaneMass.miterEdge;
    const subdomainWallMassId = `${wallId}:${subdomain.arrayIndexU}:${subdomain.arrayIndexV}`;

    if (subdomain.roofClippingOperations.length > 0) {
      allRoofClippingOps.push(...subdomain.roofClippingOperations);
      roofClippingOpsBySubMassId.set(subdomainWallMassId, subdomain.roofClippingOperations);
    }
    if (subdomain.stairClippingOperations.length > 0) {
      allStairClippingOps.push(...subdomain.stairClippingOperations);
      stairClippingOpsBySubMassId.set(subdomainWallMassId, subdomain.stairClippingOperations);
    }

    const coreMass = makeLegacySolidWallMassFromWallPlaneMass(
      subdomainWallMassId,
      level,
      halfEdgeIndex,
      wallPlaneMass,
      wallPlane.descriptor,
      buildingPlane.planeFace.plane,
      wallCorePredicate,
      "CORE",
      rooms,
      buildingGraph,
      levelPlanarGraph,
    );

    const claddingMass = makeLegacySolidWallMassFromWallPlaneMass(
      subdomainWallMassId,
      level,
      halfEdgeIndex,
      wallPlaneMass,
      wallPlane.descriptor,
      buildingPlane.planeFace.plane,
      wallCladdingPredicate,
      "CLADDING",
      rooms,
      buildingGraph,
      levelPlanarGraph,
    );

    if (!coreMass || !claddingMass) {
      continue;
    }

    tagSolidWallMassSubMassId(coreMass, subdomainWallMassId);
    tagSolidWallMassSubMassId(claddingMass, subdomainWallMassId);

    legacyCoreMasses.push(coreMass);
    legacyCladdingMasses.push(claddingMass);
  }

  // LOD0

  const wallMassBase = isFreeform
    ? makeLegacyBaseWallMassFromFreeformSegment(wallId, level, wallPlaneMasses, rooms, buildingGraph)
    : makeLegacyBaseWallMassFromHalfEdge(
        wallId,
        level,
        // levelPlanarGraph is always provided on the room path (isFreeform is false)
        levelPlanarGraph as PolygonPlanarGraph,
        halfEdgeIndex,
        wallPlaneMasses,
        rooms,
        buildingGraph,
      );

  if (!wallMassBase || wallMassBase.wallType === WallType.VOID) {
    // TODO - double check that we don't need to add layers here
    return wallMassBase;
  }

  // LOD1

  const wallStructureLOD1 = wallLayerNew(WallLayerType.STRUCTURE, { ...wallMassBase, layers: [] });
  wallMassBase.layers.push(wallStructureLOD1);

  const wallSubstrateLOD1 = wallLayerNew(WallLayerType.SUBSTRATE, { ...wallMassBase, layers: [] });
  wallMassBase.layers.push(wallSubstrateLOD1);

  const wallFinishLOD1 = wallLayerNew(WallLayerType.FINISH, { ...wallMassBase, layers: [] });
  wallMassBase.layers.push(wallFinishLOD1);

  // LOD2

  // TODO - ensure the wall masses are ordered on the half edge?
  // halfEdgeLegacyWallMasses.sort((a, b) => a.section.startOffset - b.section.startOffset);

  for (let i = 0; i < legacyCoreMasses.length; i++) {
    const wallMass = legacyCoreMasses[i];

    const wallStructureLOD2 = wallLayerNew(WallLayerType.STRUCTURE, wallMass);
    wallStructureLOD1.layers.push(wallStructureLOD2);

    const wallSubstrateLOD2 = wallLayerNew(WallLayerType.SUBSTRATE, wallMass);
    wallSubstrateLOD1.layers.push(wallSubstrateLOD2);
  }

  for (let i = 0; i < legacyCladdingMasses.length; i++) {
    const wallMass = legacyCladdingMasses[i];

    const wallFinishLOD2 = wallLayerNew(WallLayerType.FINISH, wallMass);
    wallFinishLOD1.layers.push(wallFinishLOD2);
  }

  return {
    ...wallMassBase,
    roofClippingOperations: allRoofClippingOps,
    roofClippingOpsBySubMassId,
    stairClippingOperations: allStairClippingOps,
    stairClippingOpsBySubMassId,
  };
}

const VOID_ROOM_DESCRIPTOR: WallSideRoomDescriptor = {
  roomLevelId: "VOID_LEVEL",
  roomKind: RoomKind.BOUNDARY,
  roomClass: RoomClass.EXTERIOR,
  roomEnclosure: RoomEnclosure.NONE,
  roomSupport: RoomSupport.UNSUPPORTED,
  roomCover: RoomCover.UNCOVERED,
  roomWallOperators: [],
};

const VOID_FOUNDATION_ASSEMBLY: FoundationAssembly = {
  assemblyType: FoundationAssemblyType.VOID,
  lateralDatumAbstraction: new LegacyFoundationLateralDatumAbstraction(
    foundationLateralDatumEntityNew({
      sortIndex: 0,
      id: "VOID_FOUNDATION_LATERAL_DATUM",
      name: "VOID_FOUNDATION_LATERAL_DATUM",
      justification: LateralDatumJustification.INSIDE_TO_OUTSIDE,
      anchor: WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING,
      offset: 0,
    }),
  ),
};

const VOID_WALL_ASSEMBLY: WallAssembly = wallAssemblyNew();
const VOID_WALL_OPERATORS: WallOperators = {
  leftFinishOperators: [],
  leftCoreOperators: [],
  rightCoreOperators: [],
  rightFinishOperators: [],
};

function makeLegacyBaseWallMassFromHalfEdge(
  wallId: string,
  level: Level,
  levelPlanarGraph: PolygonPlanarGraph,
  halfEdgeIndex: number,
  halfEdgeWallPlaneMasses: WallPlaneSubdomainMiterMass[],
  rooms: Table<Room>,
  buildingGraph: BuildingGraph,
): WallMass {
  const [halfEdgeStartX, halfEdgeStartY] = planarGraphHalfEdgeStart(levelPlanarGraph.planarGraph, halfEdgeIndex);
  const [halfEdgeEndX, halfEdgeEndY] = planarGraphHalfEdgeEnd(levelPlanarGraph.planarGraph, halfEdgeIndex);

  const bottomOffset = levelInchesNew(0);
  const topOffset = levelInchesNew(level.topOfWall);
  const baseOffset = bottomOffset;
  const startOffset = 0;
  const endOffset = 1;

  const wallCondition = WallCondition.INTERIOR_INTERIOR;

  if (!halfEdgeWallPlaneMasses.length) {
    const wallAssembly = VOID_WALL_ASSEMBLY;
    const wallSection: WallSection = {
      leftSide: {
        structure: StructuralAssemblyType.WALL,
        cladding: WallCladdingType.NONE,
      },
      rightSide: {
        structure: StructuralAssemblyType.WALL,
        cladding: WallCladdingType.NONE,
      },
      startOffset,
      endOffset,
      bottomOffset,
      topOffset,
    };

    const leftSideRoom = VOID_ROOM_DESCRIPTOR;
    const leftSide = makeLegacyWallSideDescriptor(level, leftSideRoom);

    const rightSideRoom = VOID_ROOM_DESCRIPTOR;
    const rightSide = makeLegacyWallSideDescriptor(level, rightSideRoom);

    const wallDescriptor: WallDescriptor = {
      leftSide,
      rightSide,
      floorPriority: PathPriority.NONE,
      foundationCondition: FoundationCondition.HOUSE_EXTERIOR,
      foundationAssembly: VOID_FOUNDATION_ASSEMBLY,
      wallOperators: VOID_WALL_OPERATORS,
    };

    const wallPath = makeLegacyWallPath(
      baseOffset,
      halfEdgeIndex,
      halfEdgeStartX,
      halfEdgeStartY,
      halfEdgeEndX,
      halfEdgeEndY,
      wallDescriptor,
      wallCondition,
      WallType.VOID,
    );

    return voidWallFromHalfEdgeOffsets(wallId, wallPath, wallAssembly, level.floorElevation, wallSection, {
      leftOffset: 0,
      rightOffset: 0,
    });
  }

  const wallBaseMass = halfEdgeWallPlaneMasses[0];
  const pathStartX = halfEdgeStartX;
  const pathStartY = halfEdgeStartY;
  const pathEndX = halfEdgeEndX;
  const pathEndY = halfEdgeEndY;

  const [pTx, pTy] = line2Tangent(halfEdgeStartX, halfEdgeStartY, halfEdgeEndX, halfEdgeEndY);
  const [eSx, eSy, eEx, eEy] = wallBaseMass.miterEdge.edge;
  const [eTx, eTy] = line2Tangent(eSx, eSy, eEx, eEy);
  // Canonical wall-plane U can oppose the planar-graph half-edge. Keep path.halfEdgeIndex on the
  // PG half-edge (legacy invariant) and remap plane left/right into that path frame instead of
  // retargeting the half-edge to the opposite twin.
  const edgeReversed = vec2Dot(eTx, eTy, pTx, pTy) < 0;

  let wallDescriptor = makeLegacyWallDescriptorFromWallPlaneMass(wallBaseMass, level, rooms, buildingGraph);
  if (edgeReversed) {
    wallDescriptor = wallDescriptorOpposite(wallDescriptor);
  }

  const wallPath = makeLegacyWallPath(
    baseOffset,
    halfEdgeIndex,
    pathStartX,
    pathStartY,
    pathEndX,
    pathEndY,
    wallDescriptor,
    wallCondition,
    WallType.SOLID,
  );

  let leftOffset = -Infinity;
  let rightOffset = -Infinity;
  for (const halfEdgeWallPlaneMass of halfEdgeWallPlaneMasses) {
    const { leftCoreOffset: planeLeft, rightCoreOffset: planeRight } = halfEdgeWallPlaneMass.miterEdge;
    const left = edgeReversed ? planeRight : planeLeft;
    const right = edgeReversed ? planeLeft : planeRight;
    leftOffset = Math.max(leftOffset, left);
    rightOffset = Math.max(rightOffset, right);
  }

  let wallAssembly = makeLegacyWallAssemblyFromWallPlaneMass(wallBaseMass);
  let wallSection = makeLegacyWallSectionFromWallPlaneMass(
    wallBaseMass,
    "CLADDING",
    startOffset,
    endOffset,
    bottomOffset,
    topOffset,
  );
  if (edgeReversed) {
    wallAssembly = wallAssemblyOpposite(wallAssembly);
    wallSection = wallSectionOppositeSides(wallSection);
  }

  return makeLegacySolidWallMassFromEndOffsets(
    wallId,
    level,
    wallPath,
    wallAssembly,
    wallSection,
    pathStartX,
    pathStartY,
    pathEndX,
    pathEndY,
    leftOffset,
    rightOffset,
  );
}

/**
 * Shared tail of solid base-wall-mass construction, used by both the room half-edge
 * path and the freeform segment path: builds the wall path, symmetric end caps from
 * left/right core offsets, and the final mass. Callers differ only in how they resolve
 * path start/end and whether the wall-plane's canonical U needs remapping against the
 * path direction (`edgeReversed`) — both are already baked into `wallPath`/`wallAssembly`/
 * `wallSection` by the time this is called.
 */
function makeLegacySolidWallMassFromEndOffsets(
  wallId: string,
  level: Level,
  wallPath: WallPath,
  wallAssembly: WallAssembly,
  wallSection: WallSection,
  pathStartX: number,
  pathStartY: number,
  pathEndX: number,
  pathEndY: number,
  leftOffset: number,
  rightOffset: number,
): WallMass {
  const [normalX, normalY] = vec2Normal(pathStartX, pathStartY, pathEndX, pathEndY);
  const startCap = wallEndCapNew(
    [
      pathStartX + normalX * leftOffset,
      pathStartY + normalY * leftOffset,
      pathStartX,
      pathStartY,
      pathStartX - normalX * rightOffset,
      pathStartY - normalY * rightOffset,
    ],
    false,
    undefined,
    undefined,
    leftOffset,
    rightOffset,
    0,
    0,
    0,
    0,
  );

  const endCap = wallEndCapNew(
    [
      pathEndX - normalX * rightOffset,
      pathEndY - normalY * rightOffset,
      pathEndX,
      pathEndY,
      pathEndX + normalX * leftOffset,
      pathEndY + normalY * leftOffset,
    ],
    false,
    undefined,
    undefined,
    rightOffset,
    leftOffset,
    0,
    0,
    0,
    0,
  );

  const type: BuildingGeomParentType = "WALL_LAYER";
  return solidWallMassFromWallEnds(
    wallId,
    type,
    wallPath,
    wallAssembly,
    level.floorElevation,
    startCap,
    endCap,
    wallSection,
  );
}

/**
 * Union subdomain miter edges into one path span along the freeform segment direction.
 * Subdomains may be split (e.g. half off-room / half on-room overlap); the solid mass path
 * must cover the full authored freeform edge.
 *
 * Can't reuse the room path (`planarGraphHalfEdgeStart`/`End`): a freeform segment has no
 * planar-graph half-edge to look up, only the miter edges its subdomains were split into.
 */
function freeformSegmentPathEndpoints(
  segmentWallPlaneMasses: WallPlaneSubdomainMiterMass[],
): [number, number, number, number] | undefined {
  const [originX, originY, endX, endY] = segmentWallPlaneMasses[0].miterEdge.edge;
  if (vec2DistSquared(originX, originY, endX, endY) < DIST_EPSILON_SQUARED) {
    return undefined;
  }
  const [tangentX, tangentY] = line2Tangent(originX, originY, endX, endY);
  let minT = Infinity;
  let maxT = -Infinity;
  let pathStartX = originX;
  let pathStartY = originY;
  let pathEndX = endX;
  let pathEndY = endY;

  for (const mass of segmentWallPlaneMasses) {
    const [sx, sy, ex, ey] = mass.miterEdge.edge;
    for (const [x, y] of [
      [sx, sy],
      [ex, ey],
    ] as const) {
      const t = (x - originX) * tangentX + (y - originY) * tangentY;
      if (t < minT) {
        minT = t;
        pathStartX = x;
        pathStartY = y;
      }
      if (t > maxT) {
        maxT = t;
        pathEndX = x;
        pathEndY = y;
      }
    }
  }

  return [pathStartX, pathStartY, pathEndX, pathEndY];
}

function makeLegacyBaseWallMassFromFreeformSegment(
  wallId: string,
  level: Level,
  segmentWallPlaneMasses: WallPlaneSubdomainMiterMass[],
  rooms: Table<Room>,
  buildingGraph: BuildingGraph,
): WallMass | null {
  if (segmentWallPlaneMasses.length === 0) {
    return null;
  }

  const bottomOffset = levelInchesNew(0);
  const topOffset = levelInchesNew(level.topOfWall);
  const baseOffset = bottomOffset;
  const startOffset = 0;
  const endOffset = 1;

  const wallBaseMass = segmentWallPlaneMasses[0];
  const pathEndpoints = freeformSegmentPathEndpoints(segmentWallPlaneMasses);
  if (!pathEndpoints) {
    return null;
  }
  const [pathStartX, pathStartY, pathEndX, pathEndY] = pathEndpoints;

  const wallDescriptor = makeLegacyWallDescriptorFromWallPlaneMass(wallBaseMass, level, rooms, buildingGraph);
  const wallCondition = determineWallCondition(wallDescriptor.leftSide.wallRoom, wallDescriptor.rightSide.wallRoom);
  const wallPath = makeLegacyWallPath(
    baseOffset,
    FREEFORM_WALL_HALF_EDGE_INDEX,
    pathStartX,
    pathStartY,
    pathEndX,
    pathEndY,
    wallDescriptor,
    wallCondition,
    WallType.SOLID,
  );

  let leftOffset = -Infinity;
  let rightOffset = -Infinity;
  for (const wallPlaneMass of segmentWallPlaneMasses) {
    const { leftCoreOffset: left, rightCoreOffset: right } = wallPlaneMass.miterEdge;
    leftOffset = Math.max(leftOffset, left);
    rightOffset = Math.max(rightOffset, right);
  }

  const wallAssembly = makeLegacyWallAssemblyFromWallPlaneMass(wallBaseMass);
  const wallSection = makeLegacyWallSectionFromWallPlaneMass(
    wallBaseMass,
    "CLADDING",
    startOffset,
    endOffset,
    bottomOffset,
    topOffset,
  );

  return makeLegacySolidWallMassFromEndOffsets(
    wallId,
    level,
    wallPath,
    wallAssembly,
    wallSection,
    pathStartX,
    pathStartY,
    pathEndX,
    pathEndY,
    leftOffset,
    rightOffset,
  );
}

function makeLegacySolidWallMassFromWallPlaneMass(
  wallId: string,
  level: Level,
  levelPlanarGraphHalfEdgeIndex: number,
  wallPlaneMass: WallPlaneSubdomainMiterMass,
  wallPlaneDescriptor: WallPlaneDescriptor,
  wallPlanePlane: Plane,
  wallPlaneSubdomainOffsetPredicate: WallPlaneSubdomainOffsetPredicate,
  wallPlaneSubdomainPredicateType: WallPlanePredicateConversionType,
  rooms: Table<Room>,
  buildingGraph: BuildingGraph,
  levelPlanarGraph: PolygonPlanarGraph | undefined,
): SolidWallMass | null {
  const offsetInfo = makeLegacyWallPathOffsetInfoFromWallPlaneMass(
    wallPlaneMass,
    wallPlaneDescriptor,
    wallPlanePlane,
    wallPlaneSubdomainOffsetPredicate,
  );

  if (!offsetInfo) {
    return null;
  }

  const { bottom, top } = wallPlaneMass;

  const bottomOffset = levelInchesNew(bottom - level.floorElevation);
  const topOffset = levelInchesNew(top - level.floorElevation);
  const baseOffset = bottomOffset;

  let { startOffset, endOffset } = offsetInfo;
  let { startCap, startX, startY, endCap, endX, endY } = offsetInfo;
  let wallDescriptor = makeLegacyWallDescriptorFromWallPlaneMass(wallPlaneMass, level, rooms, buildingGraph);
  let wallAssembly = makeLegacyWallAssemblyFromWallPlaneMass(wallPlaneMass);

  const resolvedHalfEdgeIndex = levelPlanarGraphHalfEdgeIndex;
  // Plane-U path/caps are built in canonical wall-plane frame. When that opposes the PG
  // half-edge, reverse into the half-edge frame and swap sides — do not retarget halfEdgeIndex.
  let edgeReversed = false;
  // FREEFORM_WALL_HALF_EDGE_INDEX hazard: skip PG half-edge reversal for freeform subdomain
  // masses; there's no room half-edge to reconcile plane-U direction against.
  if (resolvedHalfEdgeIndex !== FREEFORM_WALL_HALF_EDGE_INDEX && levelPlanarGraph) {
    const { planarGraph } = levelPlanarGraph;
    const [pSx, pSy] = planarGraphHalfEdgeStart(planarGraph, resolvedHalfEdgeIndex);
    const [pEx, pEy] = planarGraphHalfEdgeEnd(planarGraph, resolvedHalfEdgeIndex);
    const [pTx, pTy] = line2Tangent(pSx, pSy, pEx, pEy);
    const [eTx, eTy] = line2Tangent(startX, startY, endX, endY);

    edgeReversed = vec2Dot(eTx, eTy, pTx, pTy) < 0;
  }
  if (edgeReversed) {
    const prevStartX = startX;
    const prevStartY = startY;
    startX = endX;
    startY = endY;
    endX = prevStartX;
    endY = prevStartY;

    const prevStartCap = startCap;
    startCap = endCap;
    endCap = prevStartCap;

    const prevStartOffset = startOffset;
    startOffset = 1 - endOffset;
    endOffset = 1 - prevStartOffset;

    wallDescriptor = wallDescriptorOpposite(wallDescriptor);
    wallAssembly = wallAssemblyOpposite(wallAssembly);
  }

  let wallSection = makeLegacyWallSectionFromWallPlaneMass(
    wallPlaneMass,
    wallPlaneSubdomainPredicateType,
    startOffset,
    endOffset,
    bottomOffset,
    topOffset,
  );
  if (edgeReversed) {
    wallSection = wallSectionOppositeSides(wallSection);
  }

  const wallCondition = determineWallCondition(wallDescriptor.leftSide.wallRoom, wallDescriptor.rightSide.wallRoom);

  const wallPath = makeLegacyWallPath(
    baseOffset,
    resolvedHalfEdgeIndex,
    startX,
    startY,
    endX,
    endY,
    wallDescriptor,
    wallCondition,
    WallType.SOLID,
  );

  const type: BuildingGeomParentType = "WALL_LAYER";
  return solidWallMassFromWallEnds(
    wallId,
    type,
    wallPath,
    wallAssembly,
    level.floorElevation,
    startCap,
    endCap,
    wallSection,
  );
}

/**
 * Duplicated from `wallAssemblyOpposite` in wallAssembly.ts on purpose.
 * Keep wallPlanes→legacy remapping local to this module rather than importing the shared helper.
 * Exported for unit tests of the edgeReversed remapping path.
 */
export function wallAssemblyOpposite(wallAssembly: WallAssembly): WallAssembly {
  return {
    labels: wallAssembly.labels,
    structuralLayers: wallAssembly.structuralLayers.map((layer) => ({ ...layer })).reverse(),
    structuralPriority: pathPriorityOpposite(wallAssembly.structuralPriority),
    structuralOffset: -wallAssembly.structuralOffset,
    foundationLayers: wallAssembly.foundationLayers.map((layer) => ({ ...layer })),
    leftCladdingLayers: wallAssembly.rightCladdingLayers.map((layer) => ({ ...layer })),
    rightCladdingLayers: wallAssembly.leftCladdingLayers.map((layer) => ({ ...layer })),
    extraLeftLayers: wallAssembly.extraRightLayers.map((layer) => ({ ...layer })),
    extraRightLayers: wallAssembly.extraLeftLayers.map((layer) => ({ ...layer })),
  };
}

function wallSectionOppositeSides(section: WallSection): WallSection {
  return {
    ...section,
    leftSide: section.rightSide,
    rightSide: section.leftSide,
  };
}

function makeLegacyWallDescriptorFromWallPlaneMass(
  wallPlaneMass: WallPlaneSubdomainMiterMass,
  level: Level,
  rooms: Table<Room>,
  buildingGraph: BuildingGraph,
): WallDescriptor {
  const leftSideRoom = makeWallSideRoomDescriptorFromWallPlaneMass(wallPlaneMass, rooms, buildingGraph, true);
  const leftSide = makeLegacyWallSideDescriptor(level, leftSideRoom);
  const rightSideRoom = makeWallSideRoomDescriptorFromWallPlaneMass(wallPlaneMass, rooms, buildingGraph, false);
  const rightSide = makeLegacyWallSideDescriptor(level, rightSideRoom);

  return {
    leftSide,
    rightSide,
    floorPriority: PathPriority.NONE,
    foundationCondition: FoundationCondition.HOUSE_EXTERIOR,
    foundationAssembly: VOID_FOUNDATION_ASSEMBLY,
    wallOperators: VOID_WALL_OPERATORS,
  };
}

function makeWallSideRoomDescriptorFromWallPlaneMass(
  wallPlaneMass: WallPlaneSubdomainMiterMass,
  rooms: Table<Room>,
  buildingGraph: BuildingGraph,
  isLeft: boolean,
): WallSideRoomDescriptor {
  const { miterEdge } = wallPlaneMass;

  const { faceNodeIds, faceEdgeIds } = miterEdge.subdomain;
  if (!faceNodeIds.length && !faceEdgeIds.length) {
    return VOID_ROOM_DESCRIPTOR;
  }

  const sides = resolveWallPlaneSideRoomsFromFaceNodes(faceNodeIds, buildingGraph, rooms);

  // face-node direction is outward from the room volume, so a room on the positive-normal side of
  // the wall plane sits on the left of the wall path.
  const side = isLeft ? sides.positiveSide : sides.negativeSide;
  if (!side) {
    return VOID_ROOM_DESCRIPTOR;
  }

  const { room, roomKind } = side;
  return {
    roomLevelId: room.level.id,
    roomKind,
    roomClass: room.attributes.roomClass,
    roomEnclosure: room.attributes.roomEnclosure,
    roomSupport: RoomSupport.SUPPORTED, // DUMMY
    roomCover: RoomCover.COVERED, // DUMMY,
    roomWallOperators: [],
    room,
  };
}

function makeLegacyWallPathOffsetInfoFromWallPlaneMass(
  wallPlaneMass: WallPlaneSubdomainMiterMass,
  wallPlaneDescriptor: WallPlaneDescriptor,
  wallPlanePlane: Plane,
  wallPlaneSubdomainOffsetPredicate: WallPlaneSubdomainOffsetPredicate,
) {
  const wallEndCaps = makeLegacyWallEndCapsFromWallPlaneMass(wallPlaneMass, wallPlaneSubdomainOffsetPredicate);
  if (!wallEndCaps) {
    return null;
  }

  const { startCap, endCap } = wallEndCaps;
  const { miterEdge } = wallPlaneMass;
  const { edge } = miterEdge;

  const eSx = edge[0];
  const eSy = edge[1];
  const eEx = edge[2];
  const eEy = edge[3];

  // TODO - grow the plane by the miter lengths to account for the miter edges
  // if we don't do this, the finishes will be cut off at the edge of the original plane aabb
  const planeGrowDistance = 10;
  const planeAABB = aabb2Copy(wallPlaneDescriptor.subdomainAABB);
  aabb2Grow(planeAABB, planeGrowDistance, planeGrowDistance);

  const { minX, minY, maxX, maxY } = planeAABB;

  const [startX, startY] = planeEval(wallPlanePlane, minX, minY);
  const [endX, endY] = planeEval(wallPlanePlane, maxX, maxY);
  const startOffset = closestParamNormalizedLine2(startX, startY, endX, endY, eSx, eSy, false);
  const endOffset = closestParamNormalizedLine2(startX, startY, endX, endY, eEx, eEy, false);

  return {
    startCap,
    startOffset,
    startX,
    startY,
    endCap,
    endOffset,
    endX,
    endY,
  };
}

/**
 * @NOTE - do not export this type. This should really be used
 * only locally within this file.
 */
type WallPlanePredicateConversionType = "CORE" | "CLADDING";
type WallPlaneSubdomainOffsetPredicate = (input: WallPlaneSubdomainMiterMass) => {
  leftOffset: number;
  leftEdge?: PolyLine2;
  rightOffset: number;
  rightEdge?: PolyLine2;
};

function makeLegacyWallEndCapsFromWallPlaneMass(
  wallPlaneMass: WallPlaneSubdomainMiterMass,
  wallPlaneSubdomainOffsetPredicate: WallPlaneSubdomainOffsetPredicate,
) {
  const { leftEdge, leftOffset, rightEdge, rightOffset } = wallPlaneSubdomainOffsetPredicate(wallPlaneMass);

  if (!leftEdge || !rightEdge) {
    return null;
  }

  const lastLeft = polyLine2EdgeCount(leftEdge) - 1;
  const lastRight = polyLine2EdgeCount(rightEdge) - 1;

  const [lsx, lsy] = polyLine2EdgeStart(leftEdge, 0);
  const [rsx, rsy] = polyLine2EdgeStart(rightEdge, 0);
  const [lEx, lEy] = polyLine2EdgeEnd(leftEdge, lastLeft);
  const [rEx, rEy] = polyLine2EdgeEnd(rightEdge, lastRight);

  const { miterEdge, startCap, endCap } = wallPlaneMass;
  const { edge } = miterEdge;

  // TODO - calculate the correct start and end tangents
  // we need to do this to force mitering on the start and end caps for finishes
  const [startRightTangentX, startRightTangentY] = line2Tangent(rsx, rsy, lEx, lEy);
  const [startLeftTangentX, startLeftTangentY] = line2Tangent(lsx, lsy, rEx, rEy);
  const [endRightTangentX, endRightTangentY] = line2Tangent(rEx, rEy, lsx, lsy);
  const [endLeftTangentX, endLeftTangentY] = line2Tangent(lEx, lEy, rsx, rsy);

  const start = wallEndCapNew(
    /**
     * @TODO - WALLS V2 - these verts will absolutely not work in
     * every condition, but this problem warrants a later refactor.
     * If the wall is offset such that both sides of the resolved wall are on the same side
     * of the planar graph edge, we will get self intersecting top/bottom caps.
     *
     * This problem persists even in the legacy wall generation code, and should be
     * handled as a dedicated fix in a future PR.
     */
    [lsx, lsy, edge[0], edge[1], rsx, rsy],
    startCap,
    undefined,
    undefined,
    leftOffset,
    rightOffset,
    startLeftTangentX,
    startLeftTangentY,
    startRightTangentX,
    startRightTangentY,
  );
  const end = wallEndCapNew(
    /**
     * @TODO - WALLS V2 - these verts will absolutely not work in
     * every condition, but this problem warrants a later refactor.
     * If the wall is offset such that both sides of the resolved wall are on the same side
     * of the planar graph edge, we will get self intersecting top/bottom caps.
     *
     * This problem persists even in the legacy wall generation code, and should be
     * handled as a dedicated fix in a future PR.
     */
    [rEx, rEy, edge[2], edge[3], lEx, lEy],
    endCap,
    undefined,
    undefined,
    rightOffset,
    leftOffset,
    endLeftTangentX,
    endLeftTangentY,
    endRightTangentX,
    endRightTangentY,
  );

  return { startCap: start, endCap: end };
}

// TODO - merge with makeLegacyWallSectionFromWallPlaneMass
function makeLegacyWallAssemblyFromWallPlaneMass(wallPlaneMass: WallPlaneSubdomainMiterMass): WallAssembly {
  const { miterEdge } = wallPlaneMass;
  const { topologyIds } = miterEdge.subdomain;
  const wallInfo = miterEdge.subdomain.wallInfo;

  if (!wallInfo) {
    return wallAssemblyNew();
  }

  const labels = {
    ...wallInfo.coreAssembly?.labels,
    ...wallInfo.leftCladdingAssembly?.labels,
    ...wallInfo.rightCladdingAssembly?.labels,
  };

  const parentChain: WallAssemblyLayerParentHandle[] = [];

  parentChain.push({
    type: "GENERATED_ENTITY",
    entity: {
      entityType: "WALL_PLANE",
      id: wallPlaneMass.miterEdge.wallPlaneHash,
    },
  });

  // TODO - not needed at this time
  // parentChain.push({
  //   type: "GENERATED_ENTITY",
  //   entity: {
  //     entityType: "WALL_PLANE_SUBDOMAIN",
  //     id: wallPlaneMass.miterEdge.subdomain.id,
  //     indexU: wallPlaneMass.miterEdge.subdomain.arrayIndexU,
  //     indexV: wallPlaneMass.miterEdge.subdomain.arrayIndexV,
  //     parent: {
  //       entityType: "WALL_PLANE",
  //       id: wallPlaneMass.miterEdge.wallPlaneHash,
  //     },
  //   },
  // });

  if (topologyIds.length > 0) {
    /**
     * parentChain is resolved from end -> start for specificity, so push in reverse
     * to keep topologyIds[0] as the most specific topology.
     */
    for (let i = topologyIds.length - 1; i >= 0; i--) {
      parentChain.push({
        type: "GENERATED_ENTITY",
        entity: {
          entityType: "BUILDING_GRAPH_PLANAR_TOPOLOGY",
          id: topologyIds[i],
        },
      });
    }
  }

  return wallAssemblyNew({
    labels,
    structuralPriority: PathPriority.NONE,
    leftCladdingLayers: makeLegacyWallCladdingAssemblyLayersFromWallPlaneAssembly(
      wallInfo.leftCladdingAssembly,
      wallInfo.leftCladdingSource,
      wallInfo.leftCoreToCladdingAirGap,
      parentChain,
    ) as WallAssemblyCladdingLayer[],
    structuralLayers: makeLegacyWallCoreAssemblyLayersFromWallPlaneAssembly(
      wallInfo.coreAssembly,
      wallInfo.coreSource,
      wallInfo.coreDirection,
      parentChain,
    ) as WallAssemblyStructuralLayer[],
    rightCladdingLayers: makeLegacyWallCladdingAssemblyLayersFromWallPlaneAssembly(
      wallInfo.rightCladdingAssembly,
      wallInfo.rightCladdingSource,
      wallInfo.rightCoreToCladdingAirGap,
      parentChain,
    ) as WallAssemblyCladdingLayer[],
    /**
     * NOT USED IN WALLS V2
     */
    foundationLayers: [],
    extraLeftLayers: [],
    extraRightLayers: [],
  });
}

function makeLegacyWallCladdingAssemblyLayersFromWallPlaneAssembly(
  assembly: WallPlaneAssemblyEntity | null,
  assemblySource: WallPlaneRuleHandle | undefined,
  airGapToCore: number,
  parentChain?: WallAssemblyLayerParentHandle[],
): WallAssemblyLayer[] {
  const layers: WallAssemblyLayer[] = [];

  if (airGapToCore > 0) {
    /**
     * @TODO WALLS V2 - this should be handled by injecting a layer into the substrate mass ONLY
     * in the future. Right now this results in a top finish wrap for walls with finish extended above the core
     */
    layers.push(
      wallAssemblyAirGapLayerNew({
        airGapThickness: airGapToCore,
        layerSource: assemblySource,
      }),
    );
  }

  if (assembly) {
    const assemblyLayers = sortTableBySortIndex(assembly.layers);
    accumulateLegacyWallAssemblyLayers(assemblyLayers, assemblySource, layers, parentChain);

    if (assemblyLayers.length > 0) {
      // For cladding assemblies, finish is only read on the outer layer
      const lastLayer = assemblyLayers[assemblyLayers.length - 1];
      if (wallPlaneAssemblyLayerHasFinish(lastLayer)) {
        layers.push(wallPlaneAssemblyLayerToLegacyFinishLayer(lastLayer, assemblySource, parentChain));
      }
    }
  }

  return layers;
}

function makeLegacyWallCoreAssemblyLayersFromWallPlaneAssembly(
  assembly: WallPlaneAssemblyEntity | null,
  assemblySource: WallPlaneRuleHandle | undefined,
  coreDirection: WallPlaneCoreDirection,
  parentChain?: WallAssemblyLayerParentHandle[],
): WallAssemblyLayer[] {
  const layers: WallAssemblyLayer[] = [];

  if (assembly) {
    const assemblyLayers = sortTableBySortIndex(assembly.layers);
    if (coreDirection === "RIGHT_TO_LEFT") {
      assemblyLayers.reverse();
    }

    if (assemblyLayers.length > 0) {
      // For core assemblies, finish is read on both sides of the assembly
      const firstLayer = assemblyLayers[0];
      if (wallPlaneAssemblyLayerHasFinish(firstLayer)) {
        layers.push(wallPlaneAssemblyLayerToLegacyFinishLayer(firstLayer, assemblySource, parentChain));
      }
    }

    accumulateLegacyWallAssemblyLayers(assemblyLayers, assemblySource, layers, parentChain);

    if (assemblyLayers.length > 0) {
      // For core assemblies, finish is read on both sides of the assembly
      const lastLayer = assemblyLayers[assemblyLayers.length - 1];
      if (wallPlaneAssemblyLayerHasFinish(lastLayer)) {
        layers.push(wallPlaneAssemblyLayerToLegacyFinishLayer(lastLayer, assemblySource, parentChain));
      }
    }
  }

  return layers;
}

function accumulateLegacyWallAssemblyLayers(
  assemblyLayers: WallPlaneAssemblyLayer[],
  assemblySource: WallPlaneRuleHandle | undefined,
  layersOut: WallAssemblyLayer[],
  parentChain?: WallAssemblyLayerParentHandle[],
) {
  for (let i = 0; i < assemblyLayers.length; i++) {
    const layer = assemblyLayers[i];
    layersOut.push(wallPlaneAssemblyLayerToLegacyMaterialCatalogLayer(layer, assemblySource, parentChain));
  }
}

// TODO - merge with makeLegacyWallAssemblyFromWallPlaneMass
function makeLegacyWallSectionFromWallPlaneMass(
  wallPlaneMass: WallPlaneSubdomainMiterMass,
  wallPlaneMassType: WallPlanePredicateConversionType,
  startOffset: number,
  endOffset: number,
  bottomOffset: LevelInches,
  topOffset: LevelInches,
): WallSection {
  if (!wallPlaneMass.miterEdge.subdomain.wallInfo) {
    return {
      leftSide: {
        structure: StructuralAssemblyType.NONE,
        cladding: WallCladdingType.NONE,
      },
      rightSide: {
        structure: StructuralAssemblyType.NONE,
        cladding: WallCladdingType.NONE,
      },
      startOffset,
      endOffset,
      bottomOffset,
      topOffset,
    };
  }

  const { leftCladdingAssembly, coreAssembly, coreDirection, rightCladdingAssembly } =
    wallPlaneMass.miterEdge.subdomain.wallInfo;

  let leftCladding = WallCladdingType.NONE;
  let rightCladding = WallCladdingType.NONE;

  if (wallPlaneMassType === "CORE") {
    // TODO - should only need to sort these once -
    // we should either pass the sorted layers or store them in the resolved wall plane mass as an array,
    const coreSortedLayers = coreAssembly ? sortTableBySortIndex(coreAssembly.layers) : [];
    if (coreDirection === "RIGHT_TO_LEFT") {
      coreSortedLayers.reverse();
    }

    const leftMostCoreLayer = coreSortedLayers[0];
    if (leftMostCoreLayer) {
      leftCladding = wallPlaneAssemblyLayerToLegacyWallCladdingType(leftMostCoreLayer);
    }

    const rightMostCoreLayer = coreSortedLayers[coreSortedLayers.length - 1];
    if (rightMostCoreLayer) {
      rightCladding = wallPlaneAssemblyLayerToLegacyWallCladdingType(rightMostCoreLayer);
    }
  } else {
    const leftSortedLayers = leftCladdingAssembly ? sortTableBySortIndex(leftCladdingAssembly.layers) : [];
    const leftMostLayer = leftSortedLayers[leftSortedLayers.length - 1];
    if (leftMostLayer) {
      leftCladding = wallPlaneAssemblyLayerToLegacyWallCladdingType(leftMostLayer);
    }

    const rightSortedLayers = rightCladdingAssembly ? sortTableBySortIndex(rightCladdingAssembly.layers) : [];

    const rightMostLayer = rightSortedLayers[rightSortedLayers.length - 1];
    if (rightMostLayer) {
      rightCladding = wallPlaneAssemblyLayerToLegacyWallCladdingType(rightMostLayer);
    }
  }

  return {
    leftSide: {
      structure: StructuralAssemblyType.WALL,
      cladding: leftCladding,
    },
    rightSide: {
      structure: StructuralAssemblyType.WALL,
      cladding: rightCladding,
    },
    startOffset,
    endOffset,
    bottomOffset,
    topOffset,
  };
}

function makeLegacyWallPath(
  baseOffset: LevelInches,
  halfEdgeIndex: number,
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  wallDescriptor: WallDescriptor,
  wallCondition: WallCondition,
  wallType: WallType,
): WallPath {
  const length = line2Len(startX, startY, endX, endY);

  const { leftSide, rightSide } = wallDescriptor;
  const leftWallRoom = leftSide.wallRoom;
  const rightWallRoom = rightSide.wallRoom;

  return {
    halfEdgeIndex,
    startX,
    startY,
    endX,
    endY,
    length,
    baseOffset,
    wallDescriptor,
    wallType,
    wallCondition,
    leftRoomKind: leftWallRoom.roomKind,
    leftRoom: leftWallRoom.room,
    rightRoomKind: rightWallRoom.roomKind,
    rightRoom: rightWallRoom.room,
  };
}

function makeLegacyWallSideDescriptor(level: Level, wallRoom: WallSideRoomDescriptor): WallSideDescriptor {
  const wallBottom = levelInchesNew(level.floorElevation);
  const wallTop = levelInchesNew(level.topOfWall);
  return {
    wallRoom,
    wallSideDatums: {
      foundationBottom: wallBottom,
      foundationTop: wallTop,
      floorBottom: wallBottom,
      floorTop: wallBottom,
      wallTop: wallTop,
      ceilingBottom: wallTop,
      ceilingTop: wallTop,
      roofBottom: wallTop,
      roofTop: wallTop,
    },
    wallFacadeSpans: [],
    wallFacadeSweeps: [],
    wallFacadeDatums: {},
    wallExteriorMaterial: "wallExterior0",
    floorStructuralType: StructuralFloorType.FRAMING,
    floorAssemblyType: FloorAssemblyType.VOID,
  };
}

function tagSolidWallMassSubMassId(mass: SolidWallMass, subMassId: string): void {
  const tag: BuildingGeomParent = { type: "PARENT", parentType: "WALL_SUB_MASS", parentId: subMassId };
  tagBuildingFace(mass.leftFace, tag);
  tagBuildingFace(mass.rightFace, tag);
  if (mass.startFace) {
    tagBuildingFace(mass.startFace, tag);
  }
  if (mass.endFace) {
    tagBuildingFace(mass.endFace, tag);
  }
  tagBuildingFace(mass.topFace, tag);
  tagBuildingFace(mass.bottomFace, tag);
}
